/**
 * CustomerInBodyData - InBody Body Composition Analysis Component
 * 
 * Displays InBody measurement data for a customer including:
 * - Latest measurement summary
 * - Historical data table
 * - Progress charts (weight, body fat, muscle mass)
 * - Demo data generation for testing
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Activity, TrendingUp, TrendingDown, Scale, Droplets, Dumbbell, Heart, RefreshCw, Plus, Trash2, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dark theme colors
const theme = {
  bg: '#0a0a0a',
  cardBg: '#1a1a1a',
  cardBgHover: '#252525',
  accent: '#EAB308',
  accentDark: '#CA8A04',
  text: '#ffffff',
  textMuted: '#a0a0a0',
  border: '#2a2a2a',
  success: '#22c55e',
  danger: '#ef4444',
  info: '#3b82f6'
};

const CustomerInBodyData = ({ customerId, isPortal = false, token: portalToken }) => {
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [selectedMeasurement, setSelectedMeasurement] = useState(null);

  // Get token based on context
  const getToken = () => {
    // In admin context, always use admin token first
    const adminToken = localStorage.getItem('token');
    if (adminToken && !isPortal) {
      return adminToken;
    }
    // In portal context, use portal token
    return portalToken || localStorage.getItem('portal_token') || localStorage.getItem('customerToken') || adminToken;
  };

  useEffect(() => {
    const token = getToken();
    if ((customerId || isPortal) && token) {
      fetchMeasurements();
    } else if (!token) {
      setLoading(false);
    }
  }, [customerId, isPortal]);

  const fetchMeasurements = async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const endpoint = isPortal 
        ? `${API}/inbody/portal/measurements`
        : `${API}/inbody/customers/${customerId}/measurements`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMeasurements(response.data.measurements || []);
      if (response.data.measurements?.length > 0) {
        setSelectedMeasurement(response.data.measurements[0]);
      }
    } catch (error) {
      console.error('Error fetching InBody measurements:', error);
      // Don't show error toast for 401 (auth) or 404 (no data) - just silently fail
      if (error.response?.status !== 404 && error.response?.status !== 401) {
        toast.error('Fehler beim Laden der InBody-Daten');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateDemoData = async () => {
    if (isPortal) return; // Only admin can generate demo data
    
    setGenerating(true);
    try {
      const token = getToken();
      await axios.post(
        `${API}/inbody/customers/${customerId}/demo-measurement`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Demo-Messung erfolgreich generiert');
      await fetchMeasurements();
    } catch (error) {
      console.error('Error generating demo data:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Generieren der Demo-Daten');
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteMeasurement = async (measurementId) => {
    if (isPortal) return; // Only admin can delete
    if (!window.confirm('Möchten Sie diese Messung wirklich löschen?')) return;
    
    try {
      const token = getToken();
      await axios.delete(`${API}/inbody/measurements/${measurementId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Messung gelöscht');
      await fetchMeasurements();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Prepare chart data (reverse for chronological order)
  const chartData = [...measurements].reverse().map(m => ({
    date: new Date(m.measurement_date).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' }),
    fullDate: new Date(m.measurement_date).toLocaleDateString('de-CH'),
    weight: m.weight,
    bodyFat: m.percent_body_fat,
    muscleMass: m.skeletal_muscle_mass,
    bmi: m.bmi,
    inbodyScore: m.inbody_score
  }));

  const latestMeasurement = measurements[0];

  // Calculate trends
  const calculateTrend = (field) => {
    if (measurements.length < 2) return null;
    const latest = measurements[0]?.[field];
    const previous = measurements[1]?.[field];
    if (!latest || !previous) return null;
    const diff = latest - previous;
    return { diff: diff.toFixed(1), positive: diff > 0 };
  };

  const weightTrend = calculateTrend('weight');
  const fatTrend = calculateTrend('percent_body_fat');
  const muscleTrend = calculateTrend('skeletal_muscle_mass');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-t-transparent" style={{ borderColor: theme.accent }} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-inbody-data">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: `${theme.accent}20` }}>
            <Activity size={24} style={{ color: theme.accent }} />
          </div>
          <div>
            <h3 className="font-bold text-lg" style={{ color: theme.text }}>InBody Analyse</h3>
            <p className="text-sm" style={{ color: theme.textMuted }}>
              {measurements.length > 0 
                ? `${measurements.length} Messungen vorhanden`
                : 'Noch keine Messungen vorhanden'}
            </p>
          </div>
        </div>
        
        {!isPortal && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchMeasurements}
              className="gap-1"
              style={{ borderColor: theme.border, color: theme.textMuted }}
            >
              <RefreshCw size={14} />
              Aktualisieren
            </Button>
            <Button
              size="sm"
              onClick={handleGenerateDemoData}
              disabled={generating}
              className="gap-1"
              style={{ backgroundColor: theme.accent, color: '#000' }}
              data-testid="generate-demo-btn"
            >
              {generating ? (
                <RefreshCw size={14} className="animate-spin" />
              ) : (
                <Plus size={14} />
              )}
              Demo-Daten
            </Button>
          </div>
        )}
      </div>

      {measurements.length === 0 ? (
        /* Empty State */
        <Card style={{ backgroundColor: theme.cardBg, borderColor: theme.border }}>
          <CardContent className="py-12 text-center">
            <Activity size={48} className="mx-auto mb-4" style={{ color: theme.textMuted }} />
            <h4 className="font-semibold mb-2" style={{ color: theme.text }}>
              Keine InBody-Daten vorhanden
            </h4>
            <p className="text-sm mb-4" style={{ color: theme.textMuted }}>
              {isPortal 
                ? 'Ihre Körperanalyse-Daten werden hier angezeigt, sobald eine InBody-Messung durchgeführt wurde.'
                : 'Klicken Sie auf "Demo-Daten", um Testdaten zu generieren, oder verbinden Sie ein InBody-Gerät.'}
            </p>
            {!isPortal && (
              <Button
                onClick={handleGenerateDemoData}
                disabled={generating}
                style={{ backgroundColor: theme.accent, color: '#000' }}
              >
                {generating ? 'Wird generiert...' : 'Demo-Messung erstellen'}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Latest Measurement Summary */}
          {latestMeasurement && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Weight */}
              <Card style={{ backgroundColor: theme.cardBg, borderColor: theme.border }}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Scale size={20} style={{ color: theme.accent }} />
                    {weightTrend && (
                      <Badge 
                        variant="outline" 
                        className="text-xs"
                        style={{ 
                          borderColor: weightTrend.positive ? theme.danger : theme.success,
                          color: weightTrend.positive ? theme.danger : theme.success
                        }}
                      >
                        {weightTrend.positive ? '+' : ''}{weightTrend.diff} kg
                        {weightTrend.positive ? <TrendingUp size={12} className="ml-1" /> : <TrendingDown size={12} className="ml-1" />}
                      </Badge>
                    )}
                  </div>
                  <p className="text-2xl font-bold" style={{ color: theme.text }}>
                    {latestMeasurement.weight?.toFixed(1) || '-'} <span className="text-sm font-normal" style={{ color: theme.textMuted }}>kg</span>
                  </p>
                  <p className="text-xs" style={{ color: theme.textMuted }}>Gewicht</p>
                </CardContent>
              </Card>

              {/* Body Fat */}
              <Card style={{ backgroundColor: theme.cardBg, borderColor: theme.border }}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Droplets size={20} style={{ color: theme.info }} />
                    {fatTrend && (
                      <Badge 
                        variant="outline" 
                        className="text-xs"
                        style={{ 
                          borderColor: fatTrend.positive ? theme.danger : theme.success,
                          color: fatTrend.positive ? theme.danger : theme.success
                        }}
                      >
                        {fatTrend.positive ? '+' : ''}{fatTrend.diff}%
                        {fatTrend.positive ? <TrendingUp size={12} className="ml-1" /> : <TrendingDown size={12} className="ml-1" />}
                      </Badge>
                    )}
                  </div>
                  <p className="text-2xl font-bold" style={{ color: theme.text }}>
                    {latestMeasurement.percent_body_fat?.toFixed(1) || '-'} <span className="text-sm font-normal" style={{ color: theme.textMuted }}>%</span>
                  </p>
                  <p className="text-xs" style={{ color: theme.textMuted }}>Körperfett</p>
                </CardContent>
              </Card>

              {/* Muscle Mass */}
              <Card style={{ backgroundColor: theme.cardBg, borderColor: theme.border }}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Dumbbell size={20} style={{ color: theme.success }} />
                    {muscleTrend && (
                      <Badge 
                        variant="outline" 
                        className="text-xs"
                        style={{ 
                          borderColor: muscleTrend.positive ? theme.success : theme.danger,
                          color: muscleTrend.positive ? theme.success : theme.danger
                        }}
                      >
                        {muscleTrend.positive ? '+' : ''}{muscleTrend.diff} kg
                        {muscleTrend.positive ? <TrendingUp size={12} className="ml-1" /> : <TrendingDown size={12} className="ml-1" />}
                      </Badge>
                    )}
                  </div>
                  <p className="text-2xl font-bold" style={{ color: theme.text }}>
                    {latestMeasurement.skeletal_muscle_mass?.toFixed(1) || '-'} <span className="text-sm font-normal" style={{ color: theme.textMuted }}>kg</span>
                  </p>
                  <p className="text-xs" style={{ color: theme.textMuted }}>Muskelmasse</p>
                </CardContent>
              </Card>

              {/* InBody Score */}
              <Card style={{ backgroundColor: theme.cardBg, borderColor: theme.border }}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Heart size={20} style={{ color: theme.danger }} />
                  </div>
                  <p className="text-2xl font-bold" style={{ color: theme.text }}>
                    {latestMeasurement.inbody_score || '-'} <span className="text-sm font-normal" style={{ color: theme.textMuted }}>Punkte</span>
                  </p>
                  <p className="text-xs" style={{ color: theme.textMuted }}>InBody Score</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Progress Charts */}
          {chartData.length > 1 && (
            <Card style={{ backgroundColor: theme.cardBg, borderColor: theme.border }}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2" style={{ color: theme.text }}>
                  <TrendingUp size={18} style={{ color: theme.accent }} />
                  Fortschrittsverlauf
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                      <XAxis 
                        dataKey="date" 
                        stroke={theme.textMuted}
                        tick={{ fill: theme.textMuted, fontSize: 12 }}
                      />
                      <YAxis 
                        yAxisId="left"
                        stroke={theme.textMuted}
                        tick={{ fill: theme.textMuted, fontSize: 12 }}
                      />
                      <YAxis 
                        yAxisId="right" 
                        orientation="right"
                        stroke={theme.textMuted}
                        tick={{ fill: theme.textMuted, fontSize: 12 }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: theme.cardBg, 
                          border: `1px solid ${theme.border}`,
                          borderRadius: '8px',
                          color: theme.text
                        }}
                        labelStyle={{ color: theme.textMuted }}
                      />
                      <Legend 
                        wrapperStyle={{ color: theme.textMuted }}
                      />
                      <Line 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="weight" 
                        stroke={theme.accent}
                        strokeWidth={2}
                        dot={{ fill: theme.accent, strokeWidth: 2, r: 4 }}
                        name="Gewicht (kg)"
                      />
                      <Line 
                        yAxisId="right"
                        type="monotone" 
                        dataKey="bodyFat" 
                        stroke={theme.info}
                        strokeWidth={2}
                        dot={{ fill: theme.info, strokeWidth: 2, r: 4 }}
                        name="Körperfett (%)"
                      />
                      <Line 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="muscleMass" 
                        stroke={theme.success}
                        strokeWidth={2}
                        dot={{ fill: theme.success, strokeWidth: 2, r: 4 }}
                        name="Muskelmasse (kg)"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Detailed Measurement View */}
          {latestMeasurement && (
            <Card style={{ backgroundColor: theme.cardBg, borderColor: theme.border }}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between" style={{ color: theme.text }}>
                  <div className="flex items-center gap-2">
                    <Info size={18} style={{ color: theme.accent }} />
                    Detaillierte Analyse
                  </div>
                  <span className="text-sm font-normal" style={{ color: theme.textMuted }}>
                    {new Date(latestMeasurement.measurement_date).toLocaleDateString('de-CH', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {/* Body Composition */}
                  <DetailItem label="Gewicht" value={latestMeasurement.weight} unit="kg" theme={theme} />
                  <DetailItem label="Körperfett" value={latestMeasurement.percent_body_fat} unit="%" theme={theme} />
                  <DetailItem label="Muskelmasse" value={latestMeasurement.skeletal_muscle_mass} unit="kg" theme={theme} />
                  <DetailItem label="Fettfreie Masse" value={latestMeasurement.lean_body_mass} unit="kg" theme={theme} />
                  <DetailItem label="Körperfettmasse" value={latestMeasurement.body_fat_mass} unit="kg" theme={theme} />
                  <DetailItem label="Körperwasser" value={latestMeasurement.total_body_water} unit="L" theme={theme} />
                  <DetailItem label="BMI" value={latestMeasurement.bmi} unit="" theme={theme} />
                  <DetailItem label="BMR" value={latestMeasurement.bmr} unit="kcal" theme={theme} />
                  <DetailItem label="Viszeralfett" value={latestMeasurement.visceral_fat_level} unit="Level" theme={theme} />
                  <DetailItem label="Stoffwechselalter" value={latestMeasurement.metabolic_age} unit="Jahre" theme={theme} />
                  <DetailItem label="InBody Score" value={latestMeasurement.inbody_score} unit="Pkt" theme={theme} />
                  
                  {/* Targets if available */}
                  {latestMeasurement.weight_control && (
                    <DetailItem 
                      label="Gewicht Ziel" 
                      value={latestMeasurement.weight_control} 
                      unit="kg" 
                      theme={theme}
                      highlight={latestMeasurement.weight_control < 0 ? 'success' : 'danger'}
                    />
                  )}
                  {latestMeasurement.fat_control && (
                    <DetailItem 
                      label="Fett Ziel" 
                      value={latestMeasurement.fat_control} 
                      unit="kg" 
                      theme={theme}
                      highlight={latestMeasurement.fat_control < 0 ? 'success' : 'danger'}
                    />
                  )}
                  {latestMeasurement.muscle_control && (
                    <DetailItem 
                      label="Muskel Ziel" 
                      value={latestMeasurement.muscle_control} 
                      unit="kg" 
                      theme={theme}
                      highlight={latestMeasurement.muscle_control > 0 ? 'success' : 'danger'}
                    />
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Measurement History Table */}
          <Card style={{ backgroundColor: theme.cardBg, borderColor: theme.border }}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between" style={{ color: theme.text }}>
                <span>Messungshistorie</span>
                {measurements.length > 5 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAllHistory(!showAllHistory)}
                    className="gap-1"
                    style={{ color: theme.textMuted }}
                  >
                    {showAllHistory ? 'Weniger' : 'Alle'} anzeigen
                    {showAllHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow style={{ borderColor: theme.border }}>
                      <TableHead style={{ color: theme.textMuted }}>Datum</TableHead>
                      <TableHead style={{ color: theme.textMuted }}>Gewicht</TableHead>
                      <TableHead style={{ color: theme.textMuted }}>Körperfett</TableHead>
                      <TableHead style={{ color: theme.textMuted }}>Muskeln</TableHead>
                      <TableHead style={{ color: theme.textMuted }}>BMI</TableHead>
                      <TableHead style={{ color: theme.textMuted }}>Score</TableHead>
                      <TableHead style={{ color: theme.textMuted }}>Quelle</TableHead>
                      {!isPortal && <TableHead style={{ color: theme.textMuted }}></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(showAllHistory ? measurements : measurements.slice(0, 5)).map((m, idx) => (
                      <TableRow 
                        key={m.id || idx} 
                        style={{ borderColor: theme.border }}
                        className="hover:bg-white/5"
                      >
                        <TableCell style={{ color: theme.text }}>
                          {new Date(m.measurement_date).toLocaleDateString('de-CH', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </TableCell>
                        <TableCell style={{ color: theme.text }}>{m.weight?.toFixed(1) || '-'} kg</TableCell>
                        <TableCell style={{ color: theme.text }}>{m.percent_body_fat?.toFixed(1) || '-'}%</TableCell>
                        <TableCell style={{ color: theme.text }}>{m.skeletal_muscle_mass?.toFixed(1) || '-'} kg</TableCell>
                        <TableCell style={{ color: theme.text }}>{m.bmi?.toFixed(1) || '-'}</TableCell>
                        <TableCell style={{ color: theme.text }}>{m.inbody_score || '-'}</TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline"
                            style={{ 
                              borderColor: m.source === 'demo' ? theme.accent : theme.success,
                              color: m.source === 'demo' ? theme.accent : theme.success
                            }}
                          >
                            {m.source === 'demo' ? 'Demo' : m.source === 'manual' ? 'Manuell' : 'API'}
                          </Badge>
                        </TableCell>
                        {!isPortal && (
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteMeasurement(m.id)}
                              className="h-8 w-8 p-0 hover:bg-red-500/20"
                              style={{ color: theme.danger }}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// Helper component for detail items
const DetailItem = ({ label, value, unit, theme, highlight }) => {
  if (value === null || value === undefined) return null;
  
  const highlightColor = highlight === 'success' ? theme.success : highlight === 'danger' ? theme.danger : null;
  
  return (
    <div 
      className="p-3 rounded-lg"
      style={{ backgroundColor: `${theme.border}50` }}
    >
      <p className="text-xs mb-1" style={{ color: theme.textMuted }}>{label}</p>
      <p 
        className="text-lg font-semibold"
        style={{ color: highlightColor || theme.text }}
      >
        {typeof value === 'number' ? value.toFixed(1) : value} 
        {unit && <span className="text-sm font-normal ml-1" style={{ color: theme.textMuted }}>{unit}</span>}
      </p>
    </div>
  );
};

export default CustomerInBodyData;
